/**
 * AnalyticsDashboard.tsx
 *
 * Date-range controls + auth guard for the admin analytics dashboard.
 * The actual metric sections live in their own files:
 *   - TrafficMetrics.tsx    — traffic/audience KPIs, daily chart, new/returning split
 *   - EngagementMetrics.tsx — engagement KPIs
 * Both take the same { start, end } Date range and fetch independently.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../Context/UserContext';
import TrafficMetricsSection from './TrafficMetrics';
import EngagementMetricsSection from './EngagementMetrics';
import NavigationBehaviorSection from './NavigationBehavior';

// ---------------------------------------------------------------------
// Date range presets
// ---------------------------------------------------------------------
type PresetKey = 'today' | '7d' | '30d' | '90d' | 'mtd' | 'custom';

const PRESETS: { key: PresetKey; label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: '7d', label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: '90d', label: 'Last 90 days' },
    { key: 'mtd', label: 'Month to date' },
    { key: 'custom', label: 'Custom range' },
];

function presetToRange(preset: PresetKey, customStart: string, customEnd: string): { start: Date; end: Date } {
    const end = new Date();
    const start = new Date();
    switch (preset) {
        case 'today':
            start.setHours(0, 0, 0, 0);
            break;
        case '7d':
            start.setDate(start.getDate() - 7);
            break;
        case '30d':
            start.setDate(start.getDate() - 30);
            break;
        case '90d':
            start.setDate(start.getDate() - 90);
            break;
        case 'mtd':
            start.setDate(1);
            start.setHours(0, 0, 0, 0);
            break;
        case 'custom': {
            const customEndDate = new Date(customEnd);
            // Include the entire end day rather than stopping at its midnight.
            customEndDate.setHours(23, 59, 59, 999);
            return { start: new Date(customStart), end: customEndDate };
        }
    }
    return { start, end };
}

function toDateInputValue(d: Date): string {
    return d.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------
export default function AnalyticsDashboard() {
    const navigate = useNavigate();
    const { user, hasRole } = useUser(); // TODO: swap for your actual auth hook/import
    const [preset, setPreset] = useState<PresetKey>('30d');
    const [customStart, setCustomStart] = useState<string>('');
    const [customEnd, setCustomEnd] = useState<string>('');

    const { start, end } = useMemo(() => presetToRange(preset, customStart, customEnd), [preset, customStart, customEnd]);

    // Values shown in the From/To date inputs: for a preset, reflect the range
    // it computes to; for 'custom', reflect exactly what the user typed.
    const startInputValue = preset === 'custom' ? customStart : toDateInputValue(start);
    const endInputValue = preset === 'custom' ? customEnd : toDateInputValue(end);

    const handleStartChange = (value: string) => {
        setCustomStart(value);
        setCustomEnd(endInputValue);
        setPreset('custom');
    };

    const handleEndChange = (value: string) => {
        setCustomEnd(value);
        setCustomStart(startInputValue);
        setPreset('custom');
    };

    useEffect(() => {
        // Check if user is logged in
        if (!user) {
            navigate('/login');
            return;
        }

        // Check if user has administrator role
        if (!hasRole('administrator')) {
            navigate('/');
            return;
        }
    }, [user, navigate, hasRole]);

    return (
        <div className="traffic-audience-dashboard">
            <style>{`
        /* Palette borrowed from the site's global tokens (src/index.css) so this
           panel reads as part of the same dark, teal-accented site instead of
           a dropped-in light widget. Shared by TrafficMetrics.tsx and
           EngagementMetrics.tsx — both render class names defined here. */
        .traffic-audience-dashboard {
          --ta-border: rgba(69, 186, 196, 0.22);
          --ta-border-soft: rgba(69, 186, 196, 0.14);
          --ta-surface: rgba(255, 255, 255, 0.03);
          --ta-surface-hover: rgba(69, 186, 196, 0.07);
          --ta-text: #ffffff;
          --ta-text-muted: rgba(255, 255, 255, 0.55);
          --ta-text-body: rgba(255, 255, 255, 0.72);
          --ta-up: #3ddc84;
          --ta-down: #ff6b6b;
          /* Validated pair (node scripts/validate_palette.js, dataviz skill):
             CVD ΔE 17.5 (protan) / 27.5 (tritan), normal-vision floor 23.5,
             both in the dark-mode lightness band, both >= 3:1 on the card surface. */
          --series-new: var(--primary-mid-color);
          --series-returning: #c97d1f;
        }

        .traffic-audience-dashboard {
          color: var(--ta-text);
          font-family: Poppins, sans-serif;
        }

        .traffic-audience-heading {
          font-size: clamp(1.1rem, 1.6vw, 1.6rem);
          font-weight: 600;
          color: var(--primary-light-color);
          margin: 0 0 1.1rem 0;
        }

        .preset-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.6rem;
          margin-bottom: 1.1rem;
          padding-bottom: 1.1rem;
          border-bottom: 1px solid var(--ta-border);
        }
        .preset-btn {
          font-family: Poppins, sans-serif;
          font-size: 0.85rem;
          font-weight: 500;
          padding: 0.5rem 1.1rem;
          border-radius: 8px;
          border: 1.5px solid rgba(255, 255, 255, 0.18);
          background: transparent;
          color: var(--ta-text-body);
          cursor: pointer;
          transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease, transform 0.15s ease;
        }
        .preset-btn:hover {
          border-color: var(--primary-light-color);
          background-color: rgba(69, 186, 196, 0.1);
          transform: translateY(-1px);
        }
        .preset-btn--active {
          background-color: var(--primary-mid-color);
          border-color: var(--primary-mid-color);
          color: #fff;
          font-weight: 600;
        }
        .preset-btn--active:hover {
          background-color: var(--primary-light-color);
          border-color: var(--primary-light-color);
        }

        .custom-range-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 1.25rem;
          margin-bottom: 1.5rem;
          font-size: 0.85rem;
          color: var(--ta-text-body);
        }
        .custom-range-row label {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        .custom-range-row input {
          font-family: Poppins, sans-serif;
          font-size: 0.85rem;
          padding: 0.4rem 0.6rem;
          border-radius: 6px;
          border: 1.5px solid rgba(255, 255, 255, 0.18);
          background: rgba(255, 255, 255, 0.04);
          color: var(--ta-text);
          color-scheme: dark;
        }
        .custom-range-row input:focus {
          outline: none;
          border-color: var(--primary-light-color);
        }

        .kpi-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .stat-tile {
          background: var(--ta-surface);
          border-left: 3px solid var(--primary-light-color);
          border-radius: 0 10px 10px 0;
          padding: 1rem 1.2rem;
          transition: background-color 0.2s ease;
        }
        .stat-tile:hover {
          background-color: var(--ta-surface-hover);
        }
        .stat-tile-label {
          font-size: 0.75rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--ta-text-muted);
          margin-bottom: 0.5rem;
        }
        .stat-tile-value {
          font-size: clamp(1.4rem, 2vw, 1.9rem);
          font-weight: 700;
          color: var(--ta-text);
          line-height: 1.1;
        }
        .stat-tile-delta {
          font-size: 0.78rem;
          margin-top: 0.5rem;
          font-weight: 600;
        }
        .stat-tile-delta--up { color: var(--ta-up); }
        .stat-tile-delta--down { color: var(--ta-down); }
        .stat-tile-delta--flat { color: var(--ta-text-muted); }

        .split-bar-block {
          background: var(--ta-surface);
          border-left: 3px solid var(--primary-light-color);
          border-radius: 0 10px 10px 0;
          padding: 1.2rem 1.4rem;
        }
        .engagement-block {
          margin-top: 1rem;
        }
        .engagement-block .kpi-row {
          margin-bottom: 0;
        }
        .split-bar-heading {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--ta-text);
          margin-bottom: 0.8rem;
        }

        .charts-row {
          display: flex;
          align-items: stretch;
          gap: 1rem;
        }
        .line-chart-column {
          flex: 2 1 360px;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }
        .charts-row .split-bar-block:last-child {
          flex: 1 1 240px;
        }
        @media (max-width: 800px) {
          .charts-row {
            flex-direction: column;
          }
        }

        .line-chart-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 600 / 220;
        }
        .line-chart-wrap svg {
          display: block;
          cursor: crosshair;
        }
        .line-chart-wrap svg:focus {
          outline: 2px solid var(--primary-light-color);
          outline-offset: 2px;
        }
        .line-chart-gridline {
          stroke: var(--ta-border-soft);
          stroke-width: 1;
        }
        .line-chart-area {
          fill: var(--primary-light-color);
          fill-opacity: 0.1;
          stroke: none;
        }
        .line-chart-line {
          stroke: var(--primary-light-color);
          stroke-width: 2;
          stroke-linejoin: round;
          stroke-linecap: round;
        }
        .line-chart-crosshair {
          stroke: rgba(255, 255, 255, 0.25);
          stroke-width: 1;
        }
        .line-chart-dot {
          fill: var(--primary-light-color);
          stroke: var(--background-color);
          stroke-width: 2;
        }
        .line-chart-axis-label {
          font-family: Poppins, sans-serif;
          font-size: 8px;
          fill: var(--ta-text-muted);
        }
        .line-chart-endpoint-label {
          font-family: Poppins, sans-serif;
          font-size: 9px;
          font-weight: 600;
          fill: var(--ta-text);
        }
        .line-chart-tooltip {
          position: absolute;
          transform: translate(-50%, -130%);
          background: var(--secondary-color);
          border: 1px solid var(--ta-border);
          border-radius: 6px;
          padding: 0.4rem 0.6rem;
          pointer-events: none;
          white-space: nowrap;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
        }
        .line-chart-tooltip-value {
          font-size: 0.9rem;
          font-weight: 700;
          color: var(--ta-text);
          line-height: 1.2;
        }
        .line-chart-tooltip-date {
          font-size: 0.7rem;
          color: var(--ta-text-muted);
        }
        .line-chart-table-toggle {
          margin-top: 0.9rem;
          font-size: 0.8rem;
          color: var(--ta-text-body);
        }
        .line-chart-table-toggle summary {
          cursor: pointer;
          color: var(--primary-light-color);
          font-weight: 500;
        }
        .line-chart-table-wrap {
          max-height: 220px;
          overflow-y: auto;
          margin-top: 0.6rem;
        }
        .line-chart-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8rem;
        }
        .line-chart-table th,
        .line-chart-table td {
          text-align: left;
          padding: 0.35rem 0.6rem;
          border-bottom: 1px solid var(--ta-border-soft);
        }
        .line-chart-table td:last-child,
        .line-chart-table th:last-child {
          text-align: right;
          font-variant-numeric: tabular-nums;
        }
        .line-chart-table th {
          color: var(--ta-text-muted);
          font-weight: 500;
          text-transform: uppercase;
          font-size: 0.7rem;
          letter-spacing: 0.04em;
        }
        .donut-chart-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
        }
        .donut-chart-wrap {
          position: relative;
          width: clamp(120px, 16vw, 160px);
          flex-shrink: 0;
          aspect-ratio: 1;
        }
        .donut-segment--new { stroke: var(--series-new); }
        .donut-segment--returning { stroke: var(--series-returning); }
        .donut-center-label {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          pointer-events: none;
          text-align: center;
        }
        .donut-center-value {
          font-size: clamp(1.2rem, 1.8vw, 1.6rem);
          font-weight: 700;
          color: var(--ta-text);
          line-height: 1.1;
        }
        .donut-center-caption {
          font-size: 0.7rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--ta-text-muted);
          margin-top: 0.2rem;
        }
        .split-bar-legend {
          display: flex;
          gap: 1.5rem;
          margin-top: 0.8rem;
          font-size: 0.8rem;
          color: var(--ta-text-body);
        }
        .split-bar-legend--vertical {
          flex-direction: column;
          gap: 0.7rem;
          margin-top: 0;
        }
        .split-bar-legend-item {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
        }
        .split-bar-swatch {
          width: 10px;
          height: 10px;
          border-radius: 2px;
          display: inline-block;
        }
        .split-bar-swatch--new { background: var(--series-new); }
        .split-bar-swatch--returning { background: var(--series-returning); }

        @media (max-width: 560px) {
          .donut-chart-row {
            flex-direction: column;
            align-items: center;
            gap: 1.2rem;
          }
        }

        .status-row {
          font-size: 0.85rem;
          color: var(--ta-text-body);
          margin-bottom: 0.9rem;
        }
        .status-row--error { color: var(--ta-down); }

        .nav-behavior-section {
          margin-top: 1.5rem;
        }
        .nav-behavior-heading {
          font-size: 1rem;
          font-weight: 600;
          color: var(--ta-text);
          margin: 0 0 0.8rem 0;
        }
        .nav-list-block {
          margin-top: 1rem;
        }
        .nav-list-pair-row {
          display: flex;
          align-items: stretch;
          gap: 1rem;
          margin-top: 1rem;
        }
        .nav-list-pair-row .nav-list-block {
          flex: 1 1 0;
          min-width: 0;
          margin-top: 0;
        }
        @media (max-width: 800px) {
          .nav-list-pair-row {
            flex-direction: column;
          }
        }
        .nav-list-rows {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }
        .nav-list-row {
          display: grid;
          grid-template-columns: minmax(100px, 220px) 1fr auto;
          align-items: center;
          gap: 0.7rem;
        }
        .nav-list-path {
          font-size: 0.78rem;
          color: var(--ta-text-body);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .nav-list-bar-track {
          position: relative;
          height: 14px;
          background: rgba(255, 255, 255, 0.06);
          border-radius: 4px;
          overflow: hidden;
        }
        .nav-list-bar-fill {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          background: var(--series-new);
          border-radius: 4px;
        }
        .nav-list-value {
          font-size: 0.78rem;
          font-weight: 600;
          color: var(--ta-text);
          min-width: 40px;
          text-align: right;
          font-variant-numeric: tabular-nums;
        }
        .nav-list-top-row {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 0.7rem;
        }
        .nav-list-top-value {
          font-size: clamp(1.2rem, 1.8vw, 1.6rem);
          font-weight: 700;
          color: var(--ta-text);
          line-height: 1.1;
          font-variant-numeric: tabular-nums;
          flex-shrink: 0;
        }
        .nav-list-more-toggle {
          margin-top: 0.6rem;
          font-size: 0.78rem;
          color: var(--ta-text-body);
        }
        .nav-list-more-toggle summary {
          cursor: pointer;
          color: var(--primary-light-color);
          font-weight: 500;
        }
        .nav-list-more-rows {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          margin-top: 0.6rem;
        }
        .page-flow-rows {
          display: flex;
          flex-direction: column;
          gap: 0.7rem;
          max-height: 320px;
          overflow-y: auto;
        }
        .page-flow-row {
          border-bottom: 1px solid var(--ta-border-soft);
          padding-bottom: 0.5rem;
        }
        .page-flow-meta {
          font-size: 0.72rem;
          color: var(--ta-text-muted);
          margin-bottom: 0.3rem;
        }
        .page-flow-path {
          font-size: 0.78rem;
          color: var(--ta-text-body);
          display: flex;
          flex-wrap: wrap;
          gap: 0.3rem;
        }
        .page-flow-step {
          white-space: nowrap;
        }
        .page-flow-arrow {
          color: var(--ta-text-muted);
          margin: 0 0.4rem;
        }
      `}</style>

            <h2 className="traffic-audience-heading">Traffic &amp; Audience</h2>

            <div className="preset-row">
                {PRESETS.map((p) => (
                    <button
                        key={p.key}
                        type="button"
                        className={`preset-btn ${preset === p.key ? 'preset-btn--active' : ''}`}
                        onClick={() => setPreset(p.key)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            <div className="custom-range-row">
                <label>
                    From{' '}
                    <input
                        type="date"
                        value={startInputValue}
                        max={endInputValue}
                        onChange={(e) => handleStartChange(e.target.value)}
                    />
                </label>
                <label>
                    To{' '}
                    <input
                        type="date"
                        value={endInputValue}
                        min={startInputValue}
                        onChange={(e) => handleEndChange(e.target.value)}
                    />
                </label>
            </div>

            <TrafficMetricsSection start={start} end={end} />
            <EngagementMetricsSection start={start} end={end} />
            <NavigationBehaviorSection start={start} end={end} />
        </div>
    );
}
